import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import agroIABotIcon from '@/static/icons/ic_agro_ia_bot.svg';
import {
  AGROIA_GUIDED_TOUR_MAX_AUTO_SHOWS_WITHOUT_COMPLETION,
  AGROIA_GUIDED_TOUR_MAX_IN_PROGRESS_AUTO_RESHOWS,
  AGROIA_GUIDED_TOUR_RESHOW_INTERVAL_DAYS,
  AGROIA_GUIDED_TOUR_STORAGE_KEY,
  ENABLE_AGROIA_GUIDED_TOUR,
} from '@/js/constants';

const TOUR_WAIT_TIMEOUT = 15000;
const TOUR_WAIT_INTERVAL = 120;
const DAY_IN_MS = 24 * 60 * 60 * 1000;
const TOUR_SKIP_BUTTON_CLASS = 'agroia-tour-skip-btn';
const TOUR_POPOVER_CLASS = 'agroia-tour-popover';
const TOUR_POPOVER_STYLES_ID = 'agroia-tour-popover-styles';

let activeDriver = null;
let activeDriverOwnerVm = null;
let lockedScrollY = 0;
let previousHtmlOverflow = '';
let previousBodyOverflow = '';
let previousBodyPosition = '';
let previousBodyTop = '';
let previousBodyWidth = '';
let lockedScrollableElements = [];
const TOUR_CLICK_BLOCK_EVENTS = [
  'click',
  'dblclick',
  'mousedown',
  'mouseup',
  'pointerdown',
  'pointerup',
  'touchstart',
  'touchend',
];

const TOUR_SCROLLABLE_SELECTORS = [
  '.view-main',
  '.views',
  '.page',
  '.page-content',
  '.safe-areas',
];

const SCROLL_BLOCK_KEYS = new Set([
  'ArrowUp',
  'ArrowDown',
  'PageUp',
  'PageDown',
  'Home',
  'End',
  ' ',
  'Spacebar',
]);

function preventTourScroll(event) {
  event.preventDefault();
}

function preventTourScrollKeys(event) {
  if (SCROLL_BLOCK_KEYS.has(event.key)) {
    event.preventDefault();
  }
}

function preventClicksOutsideTour(event) {
  if (!event.isTrusted) {
    return;
  }

  const targetElement =
    event.target instanceof Element
      ? event.target
      : event.target?.parentElement;

  if (targetElement?.closest?.('.driver-popover')) {
    return;
  }

  event.preventDefault();
  event.stopPropagation();
  event.stopImmediatePropagation();
}

function lockTourScroll() {
  const html = document.documentElement;
  const body = document.body;

  if (
    !html ||
    !body ||
    body.dataset.agroiaTourScrollLocked === 'true'
  ) {
    return;
  }

  lockedScrollY = window.scrollY || window.pageYOffset || 0;
  previousHtmlOverflow = html.style.overflow;
  previousBodyOverflow = body.style.overflow;
  previousBodyPosition = body.style.position;
  previousBodyTop = body.style.top;
  previousBodyWidth = body.style.width;

  html.style.overflow = 'hidden';
  body.style.overflow = 'hidden';
  body.style.position = 'fixed';
  body.style.top = `-${lockedScrollY}px`;
  body.style.width = '100%';
  body.dataset.agroiaTourScrollLocked = 'true';

  lockedScrollableElements = Array.from(
    document.querySelectorAll(TOUR_SCROLLABLE_SELECTORS.join(','))
  ).map((element) => {
    const previousOverflow = element.style.overflow;
    const previousOverflowY = element.style.overflowY;
    const previousTouchAction = element.style.touchAction;

    element.style.overflow = 'hidden';
    element.style.overflowY = 'hidden';
    element.style.touchAction = 'none';

    return {
      element,
      previousOverflow,
      previousOverflowY,
      previousTouchAction,
    };
  });

  window.addEventListener('wheel', preventTourScroll, {
    passive: false,
  });
  window.addEventListener('touchmove', preventTourScroll, {
    passive: false,
  });
  window.addEventListener('keydown', preventTourScrollKeys, {
    passive: false,
  });
  TOUR_CLICK_BLOCK_EVENTS.forEach((eventName) => {
    document.addEventListener(eventName, preventClicksOutsideTour, {
      capture: true,
      passive: false,
    });
  });
}

function unlockTourScroll() {
  const html = document.documentElement;
  const body = document.body;

  if (
    !html ||
    !body ||
    body.dataset.agroiaTourScrollLocked !== 'true'
  ) {
    return;
  }

  html.style.overflow = previousHtmlOverflow;
  body.style.overflow = previousBodyOverflow;
  body.style.position = previousBodyPosition;
  body.style.top = previousBodyTop;
  body.style.width = previousBodyWidth;
  delete body.dataset.agroiaTourScrollLocked;
  lockedScrollableElements.forEach(
    ({
      element,
      previousOverflow,
      previousOverflowY,
      previousTouchAction,
    }) => {
      element.style.overflow = previousOverflow;
      element.style.overflowY = previousOverflowY;
      element.style.touchAction = previousTouchAction;
    }
  );
  lockedScrollableElements = [];
  window.removeEventListener('wheel', preventTourScroll);
  window.removeEventListener('touchmove', preventTourScroll);
  window.removeEventListener('keydown', preventTourScrollKeys);
  TOUR_CLICK_BLOCK_EVENTS.forEach((eventName) => {
    document.removeEventListener(eventName, preventClicksOutsideTour, {
      capture: true,
    });
  });
  window.scrollTo(0, lockedScrollY);
}

function resolveTourUser(store) {
  const userState = store?.state?.user || {};
  const authenticatedUser = userState?.authenticatedUser || {};
  const candidates = [
    authenticatedUser?.username,
    authenticatedUser?.email,
    userState?.userName,
    userState?.username,
  ];

  const resolved = candidates.find(
    (value) => typeof value === 'string' && value.trim() !== ''
  );

  return resolved ? resolved.trim() : 'anonymous';
}

function getStorageKey(store) {
  return `${AGROIA_GUIDED_TOUR_STORAGE_KEY}:${resolveTourUser(
    store
  )}`;
}

function toTimestamp(value) {
  return Number.isFinite(value) && value > 0 ? value : null;
}

function normalizeTourState(rawState) {
  if (!rawState || typeof rawState !== 'object') {
    return null;
  }

  const completedAt = toTimestamp(rawState.completedAt);
  const dismissedAt = toTimestamp(rawState.dismissedAt);
  const lastShownAt = toTimestamp(rawState.lastShownAt);
  const lastAutoShownAt =
    toTimestamp(rawState.lastAutoShownAt) ||
    lastShownAt ||
    dismissedAt ||
    completedAt;
  const legacyAutoShownCount = rawState.dismissed ? 1 : 0;
  const autoShownCount = Math.max(
    0,
    Number.isFinite(rawState.autoShownCount)
      ? rawState.autoShownCount
      : legacyAutoShownCount
  );
  const inProgressAutoReshowCount = Math.max(
    0,
    Number.isFinite(rawState.inProgressAutoReshowCount)
      ? rawState.inProgressAutoReshowCount
      : 0
  );

  let status =
    typeof rawState.status === 'string' ? rawState.status : null;

  if (!status) {
    if (completedAt) {
      status = 'completed';
    } else if (rawState.dismissed || dismissedAt) {
      status = 'dismissed';
    }
  }

  return {
    ...rawState,
    completedAt,
    dismissedAt,
    lastShownAt,
    lastAutoShownAt,
    autoShownCount,
    inProgressAutoReshowCount,
    status,
  };
}

function readTourState(store) {
  try {
    const raw = localStorage.getItem(getStorageKey(store));
    return raw ? normalizeTourState(JSON.parse(raw)) : null;
  } catch (error) {
    return null;
  }
}

function writeTourState(store, payload) {
  try {
    const currentState = readTourState(store) || {};
    localStorage.setItem(
      getStorageKey(store),
      JSON.stringify({
        ...currentState,
        ...payload,
      })
    );
  } catch (error) {
    // Ignora errores de storage para no bloquear la UX del detalle.
  }
}

function hasCompletedTour(state) {
  return !!(
    state &&
    (state.status === 'completed' || state.completedAt)
  );
}

function hasInProgressTour(state) {
  return !!(state && state.status === 'in_progress');
}

function hasReachedInProgressAutoReshowLimit(state) {
  return (
    (state?.inProgressAutoReshowCount || 0) >=
    AGROIA_GUIDED_TOUR_MAX_IN_PROGRESS_AUTO_RESHOWS
  );
}

function hasReachedAutoShowLimit(state) {
  return (
    (state?.autoShownCount || 0) >=
    AGROIA_GUIDED_TOUR_MAX_AUTO_SHOWS_WITHOUT_COMPLETION
  );
}

function hasReshowCooldownExpired(state) {
  if (!state?.lastAutoShownAt) {
    return true;
  }

  return (
    Date.now() - state.lastAutoShownAt >=
    AGROIA_GUIDED_TOUR_RESHOW_INTERVAL_DAYS * DAY_IN_MS
  );
}

function sleep(ms) {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

function nextFrame() {
  return new Promise((resolve) => {
    requestAnimationFrame(() => resolve());
  });
}

async function settleUi(vm, delay = 0) {
  if (vm && typeof vm.$nextTick === 'function') {
    await new Promise((resolve) => vm.$nextTick(resolve));
  }

  await nextFrame();
  await nextFrame();

  if (delay > 0) {
    await sleep(delay);
  }
}

function getVisibleElement(selector) {
  const element = document.querySelector(selector);
  if (!element) return null;

  const rect = element.getBoundingClientRect();
  return rect.width > 0 || rect.height > 0 ? element : null;
}

async function waitForVisibleElement(selector) {
  const startedAt = Date.now();

  const poll = async () => {
    const element = getVisibleElement(selector);
    if (element) {
      return element;
    }

    if (Date.now() - startedAt >= TOUR_WAIT_TIMEOUT) {
      throw new Error(`driver_selector_timeout:${selector}`);
    }

    await sleep(TOUR_WAIT_INTERVAL);
    return poll();
  };

  return poll();
}

function track(vm, eventName, extraParams = {}) {
  if (vm && typeof vm.trackAgroIAEvent === 'function') {
    vm.trackAgroIAEvent(eventName, extraParams);
  }
}

function createStep({
  id,
  element,
  title,
  description,
  side = 'bottom',
  align = 'start',
  onNext,
  disablePrevious = false,
}) {
  return {
    id,
    element,
    onNext,
    disablePrevious,
    popover: {
      title,
      description,
      side,
      align,
      disableButtons: disablePrevious ? ['previous'] : [],
    },
  };
}

async function ensureReportsReadyForTour(vm) {
  if (vm.showGenerateInterpretationButton) {
    await vm.generateInterpretationAndDiagnosis();
  }

  if (vm.showGenerateLimitsButton) {
    await vm.generateLimitsAndCorrections();
  }

  vm.showInterpretationAndDiagnosisText = true;
  vm.showLimitsAndCorrectionsText = false;

  await settleUi(vm, 180);
  await waitForVisibleElement(
    '[data-tour-id="agroia-reports-section"]'
  );
}

function getReportActionsStepElement() {
  return (
    getVisibleElement('[data-tour-id="agroia-report-actions"]') ||
    '[data-tour-id="agroia-actions"]'
  );
}

function renderSkipButton(popover, label, onSkip) {
  const footerButtons = popover?.footerButtons;
  if (!footerButtons) return;

  const existingButton = footerButtons.querySelector(
    `.${TOUR_SKIP_BUTTON_CLASS}`
  );
  if (existingButton) {
    existingButton.textContent = label;
    return;
  }

  const skipButton = document.createElement('button');
  skipButton.type = 'button';
  skipButton.className = TOUR_SKIP_BUTTON_CLASS;
  skipButton.textContent = label;
  skipButton.addEventListener('click', onSkip);
  footerButtons.prepend(skipButton);
}

function ensureTourPopoverStyles() {
  if (document.getElementById(TOUR_POPOVER_STYLES_ID)) {
    return;
  }

  const styleTag = document.createElement('style');
  styleTag.id = TOUR_POPOVER_STYLES_ID;
  styleTag.textContent = `
    .${TOUR_POPOVER_CLASS} {
      max-width: 500px;
      padding: 18px 18px 14px;
      border: 1px solid #cfe0f4;
      border-radius: 18px;
      box-shadow: 0 10px 42px rgba(0, 52, 106, 0.3);
      background:
        radial-gradient(circle at top right, rgba(133, 187, 35, 0.16), transparent 34%),
        linear-gradient(180deg, #ffffff 0%, #f5f9ff 100%);
    }

    .${TOUR_POPOVER_CLASS} .driver-popover-arrow {
      border-color: #ffffff;
    }

    .${TOUR_POPOVER_CLASS} .driver-popover-close-btn {
      color: #7f9dbd;
      top: 10px;
      right: 10px;
    }

    .${TOUR_POPOVER_CLASS} .driver-popover-close-btn:hover,
    .${TOUR_POPOVER_CLASS} .driver-popover-close-btn:focus {
      color: #00346a;
    }

    .${TOUR_POPOVER_CLASS} .driver-popover-title {
      margin: 0;
      color: #00346a;
      font-size: 16px;
      font-weight: 700;
      line-height: 1.35;
    }

    .${TOUR_POPOVER_CLASS} .driver-popover-description {
      margin-top: 8px;
      color: #35506d;
      font-size: 13px;
      line-height: 1.55;
    }

    .${TOUR_POPOVER_CLASS} .driver-popover-footer {
      margin-top: 16px;
      padding-top: 12px;
      border-top: 1px solid rgba(0, 52, 106, 0.08);
      gap: 8px;
    }

    .${TOUR_POPOVER_CLASS} .driver-popover-progress-text {
      color: #6e89a6;
      font-size: 12px;
      font-weight: 600;
    }

    .${TOUR_POPOVER_CLASS} .driver-popover-navigation-btns {
      gap: 6px;
    }

    .${TOUR_POPOVER_CLASS} .driver-popover-footer button {
      padding: 7px 12px;
      border-radius: 10px;
      border: 1px solid #c9d9ea;
      background: #ffffff;
      color: #00346a;
      font-size: 12px;
      font-weight: 600;
      text-shadow: none;
      box-shadow: none;
    }

    .${TOUR_POPOVER_CLASS} .driver-popover-footer button:hover,
    .${TOUR_POPOVER_CLASS} .driver-popover-footer button:focus {
      background: #eef5fc;
    }

    .${TOUR_POPOVER_CLASS} .driver-popover-next-btn {
      border-color: #00346a;
      background: #00346a;
      color: #ffffff;
    }

    .${TOUR_POPOVER_CLASS} .driver-popover-next-btn:hover,
    .${TOUR_POPOVER_CLASS} .driver-popover-next-btn:focus {
      background: #04509f;
    }

    .${TOUR_POPOVER_CLASS} .${TOUR_SKIP_BUTTON_CLASS} {
      border-color: transparent;
      background: transparent;
      color: #6e89a6;
    }

    .${TOUR_POPOVER_CLASS} .${TOUR_SKIP_BUTTON_CLASS}:hover,
    .${TOUR_POPOVER_CLASS} .${TOUR_SKIP_BUTTON_CLASS}:focus {
      background: rgba(4, 80, 159, 0.08);
      color: #00346a;
    }

    .agroia-tour-popover-header {
      display: flex;
      align-items: center;
      gap: 10px;
      margin-bottom: 20px;
      padding-right: 28px;
    }

    .agroia-tour-popover-avatar {
      width: 48px;
      height: 48px;
      border-radius: 50%;
      background: linear-gradient(180deg, #ffffff 0%, #eaf3ff 100%);
      border: 1px solid #cfe0f4;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
    }

    .agroia-tour-popover-avatar img {
      width: 36px;
      height: 36px;
      display: block;
    }

    .agroia-tour-popover-meta {
      min-width: 0;
    }

    .agroia-tour-popover-name {
      color: #00346a;
      font-size: 13px;
      font-weight: 700;
      line-height: 1.2;
    }

    .agroia-tour-popover-role {
      margin-top: 2px;
      color: #6e89a6;
      font-size: 11px;
      font-weight: 500;
      line-height: 1.2;
    }
  `;

  document.head.appendChild(styleTag);
}

function enhancePopoverWithBot(
  popover,
  { title, assistantName, helperLabel }
) {
  const wrapper = popover?.wrapper;
  if (!wrapper) return;

  ensureTourPopoverStyles();
  wrapper.classList.add(TOUR_POPOVER_CLASS);

  if (!wrapper.querySelector('.agroia-tour-popover-header')) {
    const header = document.createElement('div');
    header.className = 'agroia-tour-popover-header';
    header.innerHTML = `
      <div class="agroia-tour-popover-avatar">
        <img src="${agroIABotIcon}" alt="${assistantName}">
      </div>
      <div class="agroia-tour-popover-meta">
        <div class="agroia-tour-popover-name">${assistantName}</div>
        <div class="agroia-tour-popover-role">${helperLabel}</div>
      </div>
    `;

    wrapper.insertBefore(header, wrapper.firstChild);
  }

  if (popover.title && title) {
    popover.title.textContent = title;
  }
}

function buildSteps(vm) {
  const t = (key) => vm.$t(`agroIA.tour.${key}`);
  const steps = [
    createStep({
      id: 'welcome',
      element: '[data-tour-id="agroia-panel"]',
      title: t('actionsTitle'),
      description: t('actionsDescription'),
      side: 'top',
      align: 'center',
    }),
    createStep({
      id: 'report-actions',
      element: getReportActionsStepElement(),
      title: t('interpretationButtonTitle'),
      description: t('interpretationButtonDescription'),
      side: 'bottom',
      onNext: async (driverObj) => {
        await ensureReportsReadyForTour(vm);

        if (driverObj.isActive()) {
          driverObj.moveNext();
        }
      },
    }),
    createStep({
      id: 'reports-overview',
      element: '[data-tour-id="agroia-reports-section"]',
      title: t('combinedTitle'),
      description: t('combinedDescription'),
      side: 'top',
      disablePrevious: true,
    }),
    createStep({
      id: 'chat-trigger',
      element: '[data-tour-id="agroia-chat-trigger"]',
      title: t('chatButtonTitle'),
      description: t('chatButtonDescription'),
      side: 'bottom',
      onNext: async (driverObj) => {
        await vm.openChatPopup();
        await settleUi(vm, 180);
        await waitForVisibleElement(
          '[data-tour-id="agroia-chat-popup"]'
        );

        if (driverObj.isActive()) {
          driverObj.moveNext();
        }
      },
    }),
    createStep({
      id: 'chat-messages',
      element: '[data-tour-id="agroia-chat-messages"]',
      title: t('chatMessagesTitle'),
      description: t('chatMessagesDescription'),
      side: 'left',
      disablePrevious: true,
    }),
    createStep({
      id: 'chat-input',
      element: '[data-tour-id="agroia-chat-input"]',
      title: t('chatInputTitle'),
      description: t('chatInputDescription'),
      side: 'top',
    }),
  ];

  return steps;
}

export function shouldAutoStartAgroIAGuidedTour(
  store,
  { force = false } = {}
) {
  if (!ENABLE_AGROIA_GUIDED_TOUR) return false;
  if (force) return true;

  const state = readTourState(store);
  if (!state) return true;
  if (hasInProgressTour(state)) {
    if (!hasReachedInProgressAutoReshowLimit(state)) {
      return true;
    }

    writeTourState(store, {
      dismissed: true,
      status: 'dismissed',
      dismissedAt: Date.now(),
    });
    return false;
  }
  if (!hasCompletedTour(state) && hasReachedAutoShowLimit(state)) {
    return false;
  }

  return hasReshowCooldownExpired(state);
}

export function resetAgroIAGuidedTour(store) {
  try {
    localStorage.removeItem(getStorageKey(store));
  } catch (error) {
    // No-op
  }
}

export function stopAgroIAGuidedTour(ownerVm = null) {
  if (
    ownerVm &&
    activeDriverOwnerVm &&
    activeDriverOwnerVm !== ownerVm
  ) {
    return;
  }

  if (activeDriver && activeDriver.isActive()) {
    activeDriver.destroy();
    return;
  }

  activeDriver = null;
  activeDriverOwnerVm = null;
  unlockTourScroll();
}

export function startAgroIAGuidedTour(vm, { force = false } = {}) {
  if (
    !vm ||
    !vm.$store ||
    !vm.currentCodMuestra ||
    !shouldAutoStartAgroIAGuidedTour(vm.$store, { force })
  ) {
    return null;
  }

  if (activeDriver && activeDriver.isActive()) {
    return activeDriver;
  }

  const t = (key) => vm.$t(`agroIA.tour.${key}`);
  const steps = buildSteps(vm);

  if (!steps.length) return null;

  const currentState = readTourState(vm.$store);
  const wasPreviouslyCompleted = hasCompletedTour(currentState);
  const startedAt = Date.now();
  const isAutoStart = !force;
  const isInProgressAutoStart =
    isAutoStart && hasInProgressTour(currentState);
  const autoShownCount = isAutoStart
    ? (currentState?.autoShownCount || 0) + 1
    : currentState?.autoShownCount || 0;
  const inProgressAutoReshowCount = isInProgressAutoStart
    ? (currentState?.inProgressAutoReshowCount || 0) + 1
    : currentState?.inProgressAutoReshowCount || 0;

  writeTourState(vm.$store, {
    dismissed: false,
    status: wasPreviouslyCompleted ? 'completed' : 'in_progress',
    lastShownAt: startedAt,
    ...(isAutoStart
      ? {
          autoShownCount,
          inProgressAutoReshowCount,
          lastAutoShownAt: startedAt,
        }
      : {
          lastManualShownAt: startedAt,
        }),
  });

  let completionStatus = null;
  let completionAction = null;
  let hasTrackedEngagement = false;

  const driverObj = driver({
    overlayClickBehavior: () => {
      return false;
    },
    stagePadding: 4,
    stageRadius: 10,
    showProgress: false,
    // progressText: t('progressText'),
    nextBtnText: t('next'),
    prevBtnText: t('previous'),
    doneBtnText: t('finish'),
    onPopoverRender: (popover) => {
      enhancePopoverWithBot(popover, {
        title: popover?.title?.textContent || '',
        assistantName: vm.$t('agroIA.chatAssistantName'),
        helperLabel: t('assistantHelperLabel'),
      });
      renderSkipButton(popover, t('skip'), () => {
        completionStatus = 'dismissed';
        completionAction = 'skip_button';
        driverObj.destroy();
      });
    },
    onNextClick: async () => {
      const activeIndex = driverObj.getActiveIndex();
      if (!hasTrackedEngagement && activeIndex === 0) {
        hasTrackedEngagement = true;
        // "engaged" significa que el usuario ha pulsado "Siguiente" en la pantalla de bienvenida
        // y ha decidido empezar realmente el tutorial, en vez de cerrarlo u omitirlo.
        track(vm, 'agroia_guided_tour_engaged', {
          auto_show_count: autoShownCount,
        });
      }

      const step = driverObj.getActiveStep();

      if (step && typeof step.onNext === 'function') {
        try {
          await step.onNext(driverObj);
        } catch (error) {
          // El propio componente ya notifica el error al usuario.
        }
        return;
      }

      if (driverObj.hasNextStep()) {
        driverObj.moveNext();
        return;
      }

      completionStatus = 'completed';
      completionAction = 'finish_button';
      driverObj.destroy();
    },
    onPrevClick: () => {
      const step = driverObj.getActiveStep();
      if (step && step.disablePrevious) {
        return;
      }

      if (driverObj.hasPreviousStep()) {
        driverObj.movePrevious();
      }
    },
    onCloseClick: () => {
      completionStatus = 'dismissed';
      completionAction = 'close_button';
      driverObj.destroy();
    },
    onDestroyed: () => {
      const endedAt = Date.now();
      const endStatus =
        completionStatus === 'completed' || wasPreviouslyCompleted
          ? 'completed'
          : completionStatus === 'passive_close'
          ? currentState?.status || 'in_progress'
          : 'dismissed';

      writeTourState(vm.$store, {
        dismissed: endStatus === 'dismissed',
        status: endStatus,
        completedAt:
          endStatus === 'completed'
            ? currentState?.completedAt || endedAt
            : currentState?.completedAt || null,
        dismissedAt:
          endStatus === 'dismissed'
            ? endedAt
            : currentState?.dismissedAt,
        inProgressAutoReshowCount:
          endStatus === 'completed' ? 0 : inProgressAutoReshowCount,
        sampleCode: vm.currentCodMuestra,
      });
      activeDriver = null;
      activeDriverOwnerVm = null;
      unlockTourScroll();
      // Evento resumen del cierre del tour.
      // Mantiene el resultado final y el modo de cierre para tener trazabilidad
      // sin depender de la granularidad del resto de eventos.
      track(vm, 'agroia_guided_tour_end', {
        end_status: endStatus,
        completion_action: completionAction || undefined,
        start_mode: isAutoStart ? 'auto' : 'manual',
        auto_show_count: autoShownCount,
        step_count: steps.length,
      });

      if (completionStatus === 'completed') {
        // El usuario ha llegado al ultimo paso y pulsa "Finalizar".
        // Se envia engaged: true de forma explicita porque no se puede
        // completar el tour sin haberlo empezado antes desde la bienvenida.
        track(vm, 'agroia_guided_tour_finish', {
          engaged: true,
          auto_show_count: autoShownCount,
        });
        return;
      }

      if (
        completionAction === 'skip_button' ||
        completionAction === 'close_button' ||
        completionAction === 'overlay_click'
      ) {
        // El usuario cierra el tour sin completarlo.
        // dismiss_action diferencia si lo hizo con "Omitir", con la "x" o tocando fuera del popover.
        track(vm, 'agroia_guided_tour_dismiss', {
          dismiss_action: completionAction,
          engaged: hasTrackedEngagement,
          end_status: endStatus,
          auto_show_count: autoShownCount,
        });
      }
    },
    steps: [],
  });

  activeDriver = driverObj;
  activeDriverOwnerVm = vm;
  driverObj.setSteps(steps);

  // El tour se ha mostrado al usuario.
  // En el flujo actual esto sera "auto", porque aparece automaticamente al entrar en el detalle.
  // Se conserva start_mode por si en el futuro se habilita un relanzado manual del tutorial desde la UI.
  track(vm, 'agroia_guided_tour_start', {
    start_mode: isAutoStart ? 'auto' : 'manual',
    auto_show_count: autoShownCount,
    step_count: steps.length,
  });

  settleUi(vm, 120).then(() => {
    if (activeDriver === driverObj) {
      lockTourScroll();
      driverObj.drive();
    }
  });

  return driverObj;
}
